import {
  FlexRow,
  getFileNameFromPath,
  isValidImage,
  isValidVideo,
  StyledButton,
  useLoading,
} from '@gorila-shared-ui/components';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import { createPortal } from 'react-dom';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { getDownloadMediaUrlByKey } from '../../../services/evidenceService';
import { EvidenceMedia } from '../../../types/evidences';
import { downloadBlob } from '../../../utils/files';
import { getMediaTypeLabel } from '../../../utils/media';
import EvidenceMediaHeader from '../EvidenceMediaHeader';

type Props = {
  media: EvidenceMedia;
  source: string;
  onClose: () => void;
  isOpen: boolean;
};

const EvidenceMediaFullScreenPreviewModal = ({ media, source, onClose, isOpen }: Readonly<Props>) => {
  const { css, theme, classes, concatenateClasses } = useStyles();
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback } = useFeedback();

  const mediaStyles = css({ height: '100%', maxWidth: '100%', objectFit: 'contain' });
  const modalBodyStyles = css({ display: 'flex', justifyContent: 'center', flex: 1 });
  const modalFooterStyles = css({
    marginLeft: '0 !important',
    marginRight: '0 !important',
    paddingLeft: theme.sizing.scale800,
    paddingRight: theme.sizing.scale800,
    borderTop: `${theme.borders.border200.borderWidth} ${theme.borders.border200.borderStyle} ${theme.borders.border200.borderColor}`,
  });

  const download = async () => {
    if (!media.key) return;
    startLoading();
    const { file, error } = await getDownloadMediaUrlByKey(media.key, media.mediaOrigin);
    if (error) {
      showFailFeedback(error);
    } else if (file) {
      downloadBlob(file, getFileNameFromPath(media.key));
    }
    stopLoading();
  };

  return createPortal(
    <Modal
      animate
      closeable
      onClose={onClose}
      isOpen={isOpen}
      size="full"
      overrides={{
        Dialog: {
          style: {
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <ModalHeader>
        <FlexRow
          gap={theme.sizing.scale200}
          classNames={classes.centeredStart}
        >
          <EvidenceMediaHeader media={media} />
        </FlexRow>
      </ModalHeader>
      <ModalBody className={modalBodyStyles}>
        {isValidVideo(source) && (
          <video
            className={mediaStyles}
            autoPlay
            controls
            controlsList="nofullscreen nodownload"
          >
            <source
              src={source}
              type="video/mp4"
            />
          </video>
        )}
        {isValidImage(source) && (
          <img
            src={source}
            alt=""
            className={mediaStyles}
          />
        )}
      </ModalBody>
      <ModalFooter className={modalFooterStyles}>
        <FlexRow
          gap={theme.sizing.scale300}
          classNames={concatenateClasses(classes.centeredEnd, css({ maxWidth: '500px', margin: '0 auto' }))}
        >
          <StyledButton
            kind="secondary"
            onClick={onClose}
            full
          >
            Cerrar vista de pantalla completa
          </StyledButton>
          <StyledButton
            onClick={download}
            isLoading={loading}
            full
            disabled={!media.key}
          >
            Descargar {getMediaTypeLabel(media.key)}
          </StyledButton>
        </FlexRow>
      </ModalFooter>
    </Modal>,
    document.body,
  );
};

export default EvidenceMediaFullScreenPreviewModal;
